import { formatters } from '@seek/ca-myprofile-utils';
import { SeekPassIconVerified } from '@seek/certsy-ui';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Dialog,
  Heading,
  Stack,
  Strong,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import { useState } from 'react';

import { PERSONAL_DETAILS } from 'src/components/Router';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import { useAppConfig } from 'src/modules/appConfig';

import { useShareableProfile, useShareableProfilePage } from '../data';

import translations from './.vocab';

export const PersonalDetails = () => {
  const { t } = useTranslations(translations);
  const { brand } = useAppConfig();
  const { shareableProfile, shareableProfileCardEmpty } = useShareableProfile();
  const { isSelfView } = useShareableProfilePage();
  const { shareableProfileVerifiedIdentityDialogViewed } = useAnalytics();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const personalDetails = shareableProfile?.personalDetails;
  const firstName = personalDetails?.firstName || '';
  const lastName = personalDetails?.lastName || '';
  const name = formatters.formatName(firstName, lastName);
  const { name: verifiedName = '', isMatchingProfileName } =
    shareableProfile?.verifiedIdentity || {};

  const identityLinkButtonClicked = () => {
    setIsDialogOpen(true);
    shareableProfileVerifiedIdentityDialogViewed({ isSelfView });
  };

  const identityTextLinkButton = (linkText: React.ReactNode) => (
    <TextLinkButton
      id="verified-identity-link"
      icon={<SeekPassIconVerified />}
      weight="weak"
      onClick={identityLinkButtonClicked}
      data={{ automation: 'verified-identity-label-link' }}
    >
      {linkText}
    </TextLinkButton>
  );

  return shareableProfileCardEmpty[PERSONAL_DETAILS] ? null : (
    <Box
      id={PERSONAL_DETAILS}
      paddingBottom="xlarge"
      data-automation={PERSONAL_DETAILS}
    >
      <Stack space="medium">
        <Heading level="1" data={{ automation: 'firstname-lastname' }}>
          {name}
        </Heading>
        {verifiedName ? (
          <Text data={{ automation: 'certsy-verified-identity-indicator' }}>
            {isMatchingProfileName
              ? identityTextLinkButton(t('Identity verified'))
              : t('Identity verified as verifiedName', {
                  verifiedName,
                  Link: (v: React.ReactNode) => identityTextLinkButton(v),
                })}
          </Text>
        ) : null}
        {verifiedName ? (
          <Dialog
            id="verified-identity-dialog"
            title={t('Verified identity')}
            width="xsmall"
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            closeLabel={t('Close')}
            data={{ automation: 'verified-identity-dialog' }}
          >
            <Box paddingY="small">
              <Text>
                {t('This person has verified their identity as name.', {
                  name: verifiedName,
                  Strong: (v) => <Strong>{v}</Strong>,
                })}
              </Text>
            </Box>
            <Stack space="medium">
              <Text size="xsmall" tone="secondary">
                {t(
                  'A verified identity shows that this person has verified their identity...',
                )}
              </Text>
              <Text size="xsmall" tone="secondary">
                {t(
                  'This may be different to the name shown on their brand Profile...',
                  { brand },
                )}
              </Text>
              <Text size="xsmall" tone="secondary">
                {t(
                  'A preferred name is sometimes referred to as a nickname or a chosen name...',
                )}
              </Text>
            </Stack>
          </Dialog>
        ) : null}
      </Stack>
    </Box>
  );
};
